import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type AppNameValue,
  type InviteMobileUserRequest,
  type InviteMobileUserResponse,
  type MobileUserSummary,
} from '../client';

/** Stable query-key factory for the mobile-user admin caches. */
export const mobileUserKeys = {
  all: ['directory', 'mobile-users'] as const,
  list: (organisationId: string, app: AppNameValue) =>
    ['directory', 'mobile-users', 'list', organisationId, app] as const,
};

/**
 * Fetch the mobile users in the caller's account for an (org, app). Drives the
 * Settings → Users & Access → Mobile Users tab. `app` defaults to Safety.
 */
export function useMobileUsers(
  organisationId: string | null,
  app: AppNameValue = 'Safety',
  options?: Omit<UseQueryOptions<MobileUserSummary[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<MobileUserSummary[], Error>({
    queryKey: organisationId ? mobileUserKeys.list(organisationId, app) : mobileUserKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getMobileUsers(organisationId, app);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/** POST `/mobile-users/invite`. Invalidates the whole mobile-user cache on success. */
export function useInviteMobileUser() {
  const qc = useQueryClient();
  return useMutation<InviteMobileUserResponse, Error, InviteMobileUserRequest>({
    mutationKey: [...mobileUserKeys.all, 'invite'],
    mutationFn: (body) => directoryApi.inviteMobileUser(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}

/** POST `/mobile-users/{id}/suspend`. */
export function useSuspendMobileUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationKey: [...mobileUserKeys.all, 'suspend'],
    mutationFn: (id) => directoryApi.suspendMobileUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}

/** POST `/mobile-users/{id}/reactivate`. */
export function useReactivateMobileUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationKey: [...mobileUserKeys.all, 'reactivate'],
    mutationFn: (id) => directoryApi.reactivateMobileUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}

/**
 * DELETE `/mobile-users/{id}` — revokes the user's grant for one (org, app),
 * leaving other apps' grants intact. `app` defaults to Safety.
 */
export function useRemoveMobileUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; organisationId: string; app?: AppNameValue }>({
    mutationKey: [...mobileUserKeys.all, 'remove'],
    mutationFn: ({ id, organisationId, app = 'Safety' }) =>
      directoryApi.removeMobileUser(id, organisationId, app),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}
